import { yupResolver } from '@hookform/resolvers/yup';
import { FullLoader } from '@openx/components/core';
import { ActionCodeOperation, type ActionCodeURL, parseActionCodeURL } from 'firebase/auth';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { TIMEOUT_BEFORE_REDIRECT } from '../../../config';

import { useAuth } from '../components/AuthContext';
import { SsoLayout } from '../components/SsoLayout';

import { type FormValues, SetPasswordForm, type SetPasswordFormValues, validationSchema } from './SetPasswordForm';

const initialValues: FormValues = {
  newPassword: '',
};

export function UserManagement(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { verifyPasswordResetCode, confirmPasswordReset } = useAuth();

  const [actionCodeURL, setActionCodeURL] = useState<ActionCodeURL | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');

  const formMethods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver<FormValues>(validationSchema),
  });

  useEffect(() => {
    const verifyCode = async () => {
      try {
        const actionCodeURL = parseActionCodeURL(window.location.href);
        if (!actionCodeURL) {
          throw new Error('Malformed URL');
        }

        if (actionCodeURL.operation !== ActionCodeOperation.PASSWORD_RESET) {
          throw new Error('Unsupported operation');
        }

        setActionCodeURL(actionCodeURL);
        setUserEmail(await verifyPasswordResetCode(actionCodeURL.code));
      } catch (error) {
        if (error instanceof Error) {
          enqueueSnackbar(error.message, { variant: 'error' });
        }
        navigate(`/forgot-password${location.search}${location.hash}`);
      }
    };

    verifyCode();
  }, [verifyPasswordResetCode, enqueueSnackbar, navigate, location]);

  const handleSubmit = useCallback(
    async (values: SetPasswordFormValues) => {
      try {
        if (!actionCodeURL) {
          navigate(`/login${location.search}${location.hash}`);
          return;
        }
        const { newPassword } = values;
        const { code, continueUrl } = actionCodeURL;

        await confirmPasswordReset(code, newPassword);
        enqueueSnackbar('New password set successfully', {
          variant: 'success',
        });

        await new Promise(resolve => setTimeout(resolve, TIMEOUT_BEFORE_REDIRECT));

        if (!continueUrl) {
          navigate('/password-setup');
          return;
        }

        window.location.href = continueUrl;
      } catch (error) {
        if (error instanceof Error) {
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      }
    },
    [enqueueSnackbar, confirmPasswordReset, location, navigate, actionCodeURL]
  );

  if (!actionCodeURL || !userEmail) {
    return <FullLoader />;
  }

  return (
    <SsoLayout>
      <FormProvider {...formMethods}>
        <SetPasswordForm onSubmitHandler={formMethods.handleSubmit(handleSubmit)} userEmail={userEmail} />
      </FormProvider>
    </SsoLayout>
  );
}
